/**
 * RouterForm — Odoo-style full-screen detail view for routers.
 * Tabs: General / Routing / Interfaces / IPAM
 * SmartButtons: Vendor, Role, Interfaces, HA, Protocols, Estado
 * IPAM integration via object_type='router'
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  GlobeAltIcon,
  WifiIcon,
  CpuChipIcon,
  ArrowsRightLeftIcon,
  PlusIcon,
  PencilSquareIcon,
  SparklesIcon,
  SignalIcon,
  MapPinIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import LocationTreeSelect from './common/LocationTreeSelect';
import CiActivityPanel from './common/CiActivityPanel';
import ElementIpPanel from './ElementIpPanel';
import DeviceAccessPanel from './DeviceAccessPanel';
import NetworkConnectionsPanel from './NetworkConnectionsPanel';
import PowerConnectionsPanel from './PowerConnectionsPanel';
import SwitchPsuSection from './SwitchPsuSection';
import FormCardsShell, { Sf2Card } from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import '../styles/DetailView.css';
import './ServerFormCards.css';
import DetailCrumb from './DetailCrumb';

// ── Constants ────────────────────────────────────────────────────────────────

const VENDORS = [
  { value: 'cisco',    label: 'Cisco (ISR / ASR / Catalyst)' },
  { value: 'juniper',  label: 'Juniper (MX / SRX / EX)' },
  { value: 'mikrotik', label: 'MikroTik (RouterOS)' },
  { value: 'huawei',   label: 'Huawei (NetEngine / AR)' },
  { value: 'arista',   label: 'Arista Networks' },
  { value: 'ubiquiti', label: 'Ubiquiti (EdgeRouter / UISP)' },
  { value: 'hpe',      label: 'HPE / Aruba' },
  { value: 'nokia',    label: 'Nokia (SR OS)' },
  { value: 'paloalto', label: 'Palo Alto Networks' },
  { value: 'fortinet', label: 'Fortinet / FortiGate' },
  { value: 'generic',  label: 'Hardware genérico' },
  { value: 'other',    label: 'Otro' },
];

const ROLES = [
  { value: 'core',             label: 'Core' },
  { value: 'distribution',     label: 'Distribución' },
  { value: 'access',           label: 'Acceso' },
  { value: 'edge',             label: 'Edge / Borde' },
  { value: 'border',           label: 'Border Gateway' },
  { value: 'wan',              label: 'WAN Router' },
  { value: 'vpn_concentrator', label: 'Concentrador VPN' },
  { value: 'route_reflector',  label: 'Route Reflector' },
  { value: 'pe',               label: 'Provider Edge (PE)' },
  { value: 'ce',               label: 'Customer Edge (CE)' },
  { value: 'other',            label: 'Otro' },
];

const ROUTING_PROTOCOLS = [
  { value: 'static',  label: 'Estático' },
  { value: 'ospf',    label: 'OSPF' },
  { value: 'ospfv3',  label: 'OSPFv3' },
  { value: 'bgp',     label: 'BGP' },
  { value: 'eigrp',   label: 'EIGRP' },
  { value: 'rip',     label: 'RIP' },
  { value: 'is_is',   label: 'IS-IS' },
  { value: 'mpls',    label: 'MPLS' },
  { value: 'vrrp',    label: 'VRRP' },
  { value: 'hsrp',    label: 'HSRP' },
  { value: 'other',   label: 'Otro' },
];

const IFACE_TYPES = [
  { value: 'wan',            label: 'WAN' },
  { value: 'lan',            label: 'LAN' },
  { value: 'loopback',       label: 'Loopback' },
  { value: 'mgmt',           label: 'Management' },
  { value: 'trunk',          label: 'Trunk' },
  { value: 'point_to_point', label: 'Point-to-Point' },
  { value: 'tunnel',         label: 'Tunnel (GRE/IPsec)' },
  { value: 'vlan',           label: 'VLAN Sub-interface' },
  { value: 'lag',            label: 'LAG / Port-Channel' },
  { value: 'other',          label: 'Otro' },
];

const IFACE_TYPE_COLOR = {
  wan:            { bg: '#fef3c7', color: '#92400e' },
  lan:            { bg: '#dcfce7', color: '#15803d' },
  loopback:       { bg: '#ede9fe', color: '#5b21b6' },
  mgmt:           { bg: '#e0f2fe', color: '#0369a1' },
  trunk:          { bg: '#fce7f3', color: '#9d174d' },
  point_to_point: { bg: '#fff7ed', color: '#9a3412' },
  tunnel:         { bg: '#f0fdf4', color: '#166534' },
  vlan:           { bg: '#dbeafe', color: '#1e40af' },
  lag:            { bg: '#fef9c3', color: '#854d0e' },
  other:          { bg: '#f1f5f9', color: '#475569' },
};

const STATUS_COLORS = {
  active:      { bg: '#dcfce7', color: '#15803d', label: 'Activo' },
  standby:     { bg: '#e0f2fe', color: '#0369a1', label: 'Standby' },
  maintenance: { bg: '#fef9c3', color: '#854d0e', label: 'Mantenimiento' },
  decom:       { bg: '#fee2e2', color: '#991b1b', label: 'Decom' },
};

const EMPTY_FORM = {
  name: '', hostname: '', vendor: 'generic', model: '',
  serial_number: '', asset_tag: '', firmware_version: '', ios_version: '',
  router_role: 'access', routing_protocols: '', asn: '',
  isp_circuit_id: '',
  ha_enabled: false, ha_role: 'standalone',
  vpn_capable: false, mpls_capable: false, nat_enabled: false,
  throughput_gbps: '',
  status: 'active', notes: '',
  // Physical placement
  form_factor: 'rack',
  location_id: '', location_name: '',
  rack_id: '', rack_name: '',
  size_u: 1, position_u: '',
};

const EMPTY_IFACE = {
  name: '', iface_type: 'lan', description: '', mac_address: '',
  speed_mbps: '', vlan_id: '', subnet_id: '', enabled: true, sort_order: 0,
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function RouterForm({ router, companyId, onClose, onSave, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  const isEditing = Boolean(router?.id);

  /* Pasar de ficha a ficha sin salir al listado (§12.A5). */
  const irARouter = (destino) => {
    if (!destino || saving) return;
    navGuard.request(() => {
      setIsDirty(false);
      setError(null);
      onNavigate?.(destino);
    });
  };

  // Cambios sin guardar al pasar de ficha o cerrar. `handleSave` se define más
  // abajo: la función se evalúa al pulsar, no al montar.
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [ifaces,  setIfaces]  = useState([]);
  const [tab,     setTab]     = useState('general');
  const [accessCount, setAccessCount] = useState(0);
  const [saving,  setSaving]  = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [error,   setError]   = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  // Cambia para volver a cargar el formulario desde el registro guardado
  // («Descartar» del dock).
  const [reloadKey, setReloadKey] = useState(0);

  // Cambios sin guardar al pasar de ficha o cerrar. `handleSave` se define más
  // abajo: la función se evalúa al pulsar, no al montar.
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'este router',
    actionLabel: 'pasas a otro router o cierras la ficha',
    save: () => handleSave(),
  });
  const [suggesting, setSuggesting] = useState(false);

  // IPAM — subnets for interface linking
  const [subnets, setSubnets] = useState([]);
  // ISP circuits for linking
  const [circuits, setCircuits] = useState([]);
  // Physical placement
  const [locations,     setLocations]     = useState([]);
  const [racks,         setRacks]         = useState([]);
  const [rackFreeUnits, setRackFreeUnits] = useState(null); // null = no rack selected

  // Catalog dropdowns
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [modelOptions,        setModelOptions]        = useState([]);

  // Interface management
  const [showIfaceForm, setShowIfaceForm] = useState(false);
  const [ifaceForm, setIfaceForm]         = useState(EMPTY_IFACE);
  const [editingIface, setEditingIface]   = useState(null);
  const [ifaceSaving, setIfaceSaving]     = useState(false);
  const [ifaceError, setIfaceError]       = useState(null);

  // ── Bootstrap ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (router) {
      setForm({
        name:              router.name || '',
        hostname:          router.hostname || '',
        vendor:            router.vendor || 'generic',
        model:             router.model || '',
        serial_number:     router.serial_number || '',
        asset_tag:         router.asset_tag || '',
        firmware_version:  router.firmware_version || '',
        ios_version:       router.ios_version || '',
        router_role:       router.router_role || 'access',
        routing_protocols: router.routing_protocols || '',
        asn:               router.asn ?? '',
        isp_circuit_id:    router.isp_circuit_id ?? '',
        ha_enabled:        !!router.ha_enabled,
        ha_role:           router.ha_role || 'standalone',
        vpn_capable:       !!router.vpn_capable,
        mpls_capable:      !!router.mpls_capable,
        nat_enabled:       !!router.nat_enabled,
        throughput_gbps:   router.throughput_gbps ?? '',
        status:            router.status || 'active',
        notes:             router.notes || '',
        // Physical placement
        form_factor:  router.form_factor  || 'rack',
        location_id:  router.location_id  ?? '',
        location_name: router.location_name || '',
        rack_id:      router.rack_id      ?? '',
        rack_name:    router.rack_name    || '',
        size_u:       router.size_u       ?? 1,
        position_u:   router.position_u   ?? '',
      });
      setIfaces(router.interfaces || []);
    } else {
      setForm(EMPTY_FORM);
      setIfaces([]);
    }
    setIsDirty(false);
  }, [router, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load IPAM subnets, ISP circuits, locations and racks for dropdowns
  const loadAux = useCallback(async () => {
    if (!companyId) return;
    try {
      const [subRes, circRes, locRes, rackRes, mfrRes, modRes] = await Promise.all([
        api.getIpamSubnets(companyId).catch(() => ({ data: [] })),
        api.getIspCircuits(companyId).catch(() => ({ data: [] })),
        // getLocations(query, companyId): el company_id va en el 2º argumento
        api.getLocations('', companyId).catch(() => ({ data: [] })),
        api.getRacks(null, companyId).catch(() => ({ data: [] })),  // null = all racks for company
        api.getManufacturers(companyId, { deviceType: 'network' }).catch(() => ({ data: [] })),
        api.getDeviceModels(companyId,  { deviceType: 'network' }).catch(() => ({ data: [] })),
      ]);
      setSubnets(subRes.data || []);
      setCircuits(circRes.data || []);
      setLocations(locRes.data || []);
      setRacks(rackRes.data || []);
      setManufacturerOptions(Array.isArray(mfrRes?.data) ? mfrRes.data : []);
      setModelOptions(        Array.isArray(modRes?.data) ? modRes.data : []);
    } catch (_) {}
  }, [companyId]);

  useEffect(() => { loadAux(); }, [loadAux]);

  // When editing an existing router that already has a rack, load its free units
  // and pre-filter the rack list by its location
  useEffect(() => {
    if (!router?.rack_id || !companyId) return;
    // Fetch racks filtered by location so the dropdown shows the right list
    if (router.location_id) {
      api.getRacks(router.location_id, companyId).then(r => setRacks(r.data || [])).catch(() => {});
    }
    // Fetch rack detail for free-unit selector
    api.getRack(router.rack_id, companyId)
      .then(res => setRackFreeUnits(res.data?.available_units_front || []))
      .catch(() => {});
  }, [router?.rack_id, router?.location_id, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const sf = (k, v) => { setForm(f => ({ ...f, [k]: v })); setIsDirty(true); };

  /* Descartar: recarga los valores guardados (no cierra la ficha). */
  const handleDiscard = () => {
    setReloadKey(k => k + 1);
    setIsDirty(false);
    setError(null);
  };

  // ── Location / rack handlers ───────────────────────────────────────────────
  const handleLocationChange = async (e) => {
    const lid = e.target.value;
    const loc = locations.find(l => String(l.id) === String(lid));
    setForm(f => ({
      ...f,
      location_id:   lid,
      location_name: loc?.name || '',
      rack_id:       '',
      rack_name:     '',
      position_u:    '',
    }));
    setIsDirty(true);
    setRackFreeUnits(null);
    // Fetch racks filtered by the chosen location from the server
    try {
      const res = await api.getRacks(lid || null, companyId);
      setRacks(res.data || []);
    } catch (_) {}
  };

  const handleRackChange = async (e) => {
    const rid = e.target.value;
    const rack = racks.find(r => String(r.id) === String(rid));
    setForm(f => ({
      ...f,
      rack_id:       rid,
      rack_name:     rack?.name || '',
      location_id:   rack?.location_id ? String(rack.location_id) : f.location_id,
      location_name: rack?.location_name || f.location_name,
      position_u:    '',
    }));
    setIsDirty(true);
    setRackFreeUnits(null);
    if (rid) {
      try {
        const res = await api.getRack(rid, companyId);
        const freeUnits = res.data?.available_units_front || [];
        setRackFreeUnits(freeUnits);
      } catch (_) {}
    }
  };

  // Los racks se recargan filtrados por sede al cambiar la sede; este filtro
  // evita que se cuelen racks de otras sedes si la lista llega sin filtrar.
  const filteredRacks = form.location_id
    ? racks.filter(r => String(r.location_id) === String(form.location_id))
    : racks;

  const rangeLabel = useMemo(() => {
    const posVal  = Number(form.position_u);
    const sizeVal = Number(form.size_u);
    if (!posVal || !sizeVal) return null;
    const bottom = posVal + sizeVal - 1;
    return `U${posVal} – U${bottom}`;
  }, [form.position_u, form.size_u]);

  // Valid starting positions: all free U's where size_u consecutive units are available
  const validPositions = useMemo(() => {
    if (rackFreeUnits === null) return null; // null = rack not yet loaded, show plain input
    const sizeVal = Number(form.size_u) || 1;
    // Build adjusted free set: API free units + router's own currently-occupied units
    // so editing keeps the current position as a valid option
    const freeSet = new Set(rackFreeUnits);
    const currentU = Number(form.position_u);
    if (currentU) {
      for (let u = currentU; u < currentU + sizeVal; u++) freeSet.add(u);
    }
    // Valid start = any u in freeSet where all u..u+size-1 are also in freeSet
    return [...freeSet].sort((a, b) => a - b).filter(u => {
      for (let i = 0; i < sizeVal; i++) {
        if (!freeSet.has(u + i)) return false;
      }
      return true;
    });
  }, [rackFreeUnits, form.size_u, form.position_u]);

  const handleSuggest = async () => {
    if (!companyId) return;
    setSuggesting(true);
    try {
      const resp = await api.suggestName({ type: 'router', companyId, consume: true });
      const s = resp?.data?.suggested_name;
      if (s) sf('name', s);
    } catch (_) {}
    finally { setSuggesting(false); }
  };

  // ── Routing protocols multi-select ─────────────────────────────────────────
  const selectedProtocols = (form.routing_protocols || '').split(',').filter(Boolean);
  const toggleProtocol = (val) => {
    const set = new Set(selectedProtocols);
    if (set.has(val)) set.delete(val);
    else set.add(val);
    sf('routing_protocols', [...set].join(','));
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return false; }
    try {
      setSaving(true); setError(null);
      const payload = {
        ...form,
        throughput_gbps: form.throughput_gbps !== '' ? Number(form.throughput_gbps) : null,
        asn: form.asn !== '' ? Number(form.asn) : null,
        isp_circuit_id: form.isp_circuit_id !== '' ? Number(form.isp_circuit_id) : null,
        location_id: form.location_id !== '' ? Number(form.location_id) : null,
        rack_id: form.rack_id !== '' ? Number(form.rack_id) : null,
        position_u: form.position_u !== '' ? Number(form.position_u) : null,
        size_u: Number(form.size_u || 1),
        form_factor: form.form_factor,
      };
      let creado = null;
      if (isEditing) await api.updateRouter(router.id, payload, companyId);
      else           creado = registroGuardado(await api.createRouter(payload, companyId));
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
      setIsDirty(false);
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las pestañas que necesitan estar guardado.
      onSave?.(creado);
      return true;
    } catch (e) { setError(e.message); return false; }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar router "${form.name}"?\nSe eliminarán también todos sus interfaces y asignaciones IPAM.`)) return;
    try {
      await api.deleteRouter(router.id, companyId);
      onSave();
    } catch (e) { setError(e.message); }
  };

  // ── Interface handlers ─────────────────────────────────────────────────────
  const openNewIface = () => {
    setEditingIface(null);
    setIfaceForm(EMPTY_IFACE);
    setIfaceError(null);
    setShowIfaceForm(true);
  };

  const openEditIface = (iface) => {
    setEditingIface(iface);
    setIfaceForm({
      name:        iface.name || '',
      iface_type:  iface.iface_type || 'lan',
      description: iface.description || '',
      mac_address: iface.mac_address || '',
      speed_mbps:  iface.speed_mbps ?? '',
      vlan_id:     iface.vlan_id ?? '',
      subnet_id:   iface.subnet_id ?? '',
      enabled:     iface.enabled !== false,
      sort_order:  iface.sort_order ?? 0,
    });
    setIfaceError(null);
    setShowIfaceForm(true);
  };

  const handleIfaceSave = async () => {
    if (!ifaceForm.name.trim()) { setIfaceError('El nombre del interfaz es obligatorio'); return; }
    if (!router?.id) { setIfaceError('Guarda primero el router antes de añadir interfaces'); return; }
    try {
      setIfaceSaving(true); setIfaceError(null);
      const payload = {
        ...ifaceForm,
        speed_mbps: ifaceForm.speed_mbps !== '' ? Number(ifaceForm.speed_mbps) : null,
        vlan_id: ifaceForm.vlan_id !== '' ? Number(ifaceForm.vlan_id) : null,
        subnet_id: ifaceForm.subnet_id !== '' ? Number(ifaceForm.subnet_id) : null,
      };
      let res;
      if (editingIface) {
        res = await api.updateRouterInterface(router.id, editingIface.id, payload, companyId);
      } else {
        res = await api.addRouterInterface(router.id, payload, companyId);
      }
      setIfaces(res.data?.interfaces || []);
      setShowIfaceForm(false);
      setEditingIface(null);
    } catch (e) { setIfaceError(e.message); }
    finally { setIfaceSaving(false); }
  };

  const handleIfaceDelete = async (iface) => {
    if (!window.confirm(`¿Eliminar interfaz "${iface.name}"?`)) return;
    try {
      await api.deleteRouterInterface(router.id, iface.id, companyId);
      setIfaces(prev => prev.filter(i => i.id !== iface.id));
    } catch (e) { setError(e.message); }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const statusInfo  = STATUS_COLORS[form.status] || STATUS_COLORS.active;
  const ifaceCount  = ifaces.length;
  const vendorLabel = form.vendor || '—';
  const filteredModelOptions = useMemo(() => {
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.vendor || '').toLowerCase());
    const mid = matchedMfr?.id;
    return (modelOptions || []).filter(m => !mid || m.manufacturer_id === mid || !m.manufacturer_id);
  }, [modelOptions, manufacturerOptions, form.vendor]);
  const roleLabel   = ROLES.find(r => r.value === form.router_role)?.label || form.router_role;
  const protocolsText = selectedProtocols.length > 0
    ? selectedProtocols.map(p => ROUTING_PROTOCOLS.find(rp => rp.value === p)?.label || p).join(', ')
    : '—';

  // ── Variante tarjetas ──
  const paneClass = `dv-tab-pane${isCards ? ' sf2-pane' : ''}`;
  // En tarjetas no hay pestañas «General» ni «Ubicación»: su contenido vive en Resumen.
  const tabsRt = isCards
    ? [
        { key: 'general',    label: 'General' },
        { key: 'routing',    label: 'Routing' },
        { key: 'interfaces', label: `Interfaces (${ifaceCount})` },
        { key: 'access',     label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` },
        { key: 'ipam',       label: 'IPAM / IPs' },
        ...(isEditing ? [{ key: 'power', label: 'Alimentación' }] : []),
      ]
    : [
        { key: 'general',    label: 'General' },
        { key: 'ubicacion',  label: 'Ubicación' },
        { key: 'routing',    label: 'Routing' },
        { key: 'interfaces', label: `Interfaces (${ifaceCount})` },
        { key: 'access',     label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` },
        { key: 'ipam',       label: 'IPAM / IPs' },
        ...(isEditing ? [{ key: 'power', label: 'Alimentación' }] : []),
      ];

  /* Ubicación compartida: la pestaña de la ficha clásica y la columna derecha
     de General en tarjetas pintan el mismo bloque (§12.C4 y C15). */
  const routerUbicacionContent = (
    <>
      <div className="dv-section-header">Factor de forma</div>
      <div className="dv-field-grid" style={{ marginBottom: 24 }}>
        <div className="dv-field-group">
          <label className="dv-label">Factor de forma *</label>
          <select className="dv-select" value={form.form_factor} onChange={e => sf('form_factor', e.target.value)}>
            <option value="rack">Rack (en rack)</option>
            <option value="desktop">Sobremesa / Desktop</option>
            <option value="tower">Torre</option>
            <option value="wallmount">Montaje en pared</option>
          </select>
        </div>
        <div className="dv-field-group">
          <label className="dv-label">Altura (U)</label>
          <input
            className="dv-input"
            type="number" min="1" max="42"
            value={form.size_u}
            onChange={e => { setForm(f => ({ ...f, size_u: e.target.value, position_u: '' })); setIsDirty(true); }}
            disabled={form.form_factor !== 'rack'}
            placeholder="1"
          />
          {form.form_factor !== 'rack' && (
            <small style={{ color: '#94a3b8', fontSize: '0.75rem' }}>No aplica para factor de forma no-rack.</small>
          )}
        </div>
      </div>

      <div className="dv-section-header">Localización</div>
      <div className="dv-field-grid">
        <div className="dv-field-group">
          <label className="dv-label">Sede / Localización</label>
          <LocationTreeSelect
            locations={locations}
            value={form.location_id}
            onChange={(id) => handleLocationChange({ target: { value: id } })}
            placeholder="— Sin localización —"
          />
        </div>
        <div className="dv-field-group">
          <label className="dv-label">Rack</label>
          <select
            className="dv-select"
            value={form.rack_id}
            onChange={handleRackChange}
            disabled={form.form_factor !== 'rack'}
          >
            <option value="">— Sin rack —</option>
            {filteredRacks.map(r => (
              <option key={r.id} value={r.id}>
                {r.name}{r.location_name ? ` (${r.location_name})` : ''}
              </option>
            ))}
          </select>
          {form.form_factor !== 'rack' && (
            <small style={{ color: '#94a3b8', fontSize: '0.75rem' }}>No aplica — factor de forma no-rack.</small>
          )}
        </div>
        {form.form_factor === 'rack' && form.rack_id && (
          <div className="dv-field-group">
            <label className="dv-label">Posición U (inicio)</label>
            {validPositions !== null ? (
              validPositions.length === 0 ? (
                <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '4px 0' }}>
                  ⚠ Sin posiciones libres para {form.size_u}U en este rack.
                </p>
              ) : (
                <select
                  className="dv-select"
                  value={form.position_u}
                  onChange={e => sf('position_u', e.target.value)}
                >
                  <option value="">— Seleccionar posición libre —</option>
                  {validPositions.map(u => (
                    <option key={u} value={u}>
                      U{u} – U{u + Number(form.size_u) - 1}
                    </option>
                  ))}
                </select>
              )
            ) : (
              <input
                className="dv-input"
                type="number" min="1" max="42"
                value={form.position_u}
                onChange={e => sf('position_u', e.target.value)}
                placeholder="1"
              />
            )}
            {rangeLabel && (
              <small style={{ color: '#0369a1', fontSize: '0.75rem', fontWeight: 600 }}>
                Ocupa: {rangeLabel}
              </small>
            )}
          </div>
        )}
      </div>

      {form.location_name && (
        <div style={{ marginTop: 20, padding: '12px 16px', background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd', display: 'flex', alignItems: 'center', gap: 10 }}>
          <MapPinIcon style={{ width: 16, height: 16, color: '#0369a1', flexShrink: 0 }} />
          <div style={{ fontSize: '0.82rem', color: '#0369a1' }}>
            <strong>{form.location_name}</strong>
            {form.rack_name && <span> · Rack <strong>{form.rack_name}</strong></span>}
            {rangeLabel && <span> · {rangeLabel}</span>}
            {form.form_factor !== 'rack' && <span> · {form.form_factor}</span>}
          </div>
        </div>
      )}
    </>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="dv-overlay">

      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon />
          Routers
        </button>
        <DetailCrumb label={form.name || 'Nuevo router'} />

        {/* ── Navegación entre fichas: a la izquierda del spacer ── */}
        <FormPager
          items={navItems}
          index={navIndex}
          label="Router"
          disabled={saving}
          onChange={irARouter}
        />
        {navGuard.dialog}

        <div className="dv-topbar-spacer" />
        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>✓ Guardado</span>
        )}
        {!isCards && (
        <span
          className="dv-status-badge"
          style={{ background: statusInfo.bg, color: statusInfo.color, cursor: 'pointer' }}
          onClick={() => {
            const statuses = ['active', 'standby', 'maintenance', 'decom'];
            const idx = statuses.indexOf(form.status);
            sf('status', statuses[(idx + 1) % statuses.length]);
          }}
        >
          {statusInfo.label}
        </span>
        )}
        <div className="dv-topbar-actions">
          {error && <span style={{ color: '#dc2626', fontSize: '0.82rem' }}>{error}</span>}
          {isCards ? (
            <>
              <button className="dv-btn-discard" onClick={onClose}>
                <XMarkIcon style={{ width: 15, height: 15 }} /> Cerrar
              </button>
              {isEditing && (
                <button className="dv-btn-delete" onClick={handleDelete}>
                  <TrashIcon style={{ width: 15, height: 15 }} />
                </button>
              )}
            </>
          ) : (
          <>
          <button className="dv-btn-discard" onClick={onClose}>
            <XMarkIcon style={{ width: 15, height: 15 }} /> Descartar
          </button>
          <button className="dv-btn-save" onClick={handleSave} disabled={saving}>
            <CheckIcon style={{ width: 15, height: 15 }} />
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
          {isEditing && (
            <button className="dv-btn-delete" onClick={handleDelete}>
              <TrashIcon style={{ width: 15, height: 15 }} />
            </button>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        {/* Vendor */}
        <div className="dv-smart-btn">
          <span className="dv-smart-btn-icon">
            <CpuChipIcon style={{ width: 18, height: 18, color: '#2563eb' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {vendorLabel}
          </span>
          <span className="dv-smart-btn-label">Vendor</span>
        </div>

        <div className="dv-smart-sep" />

        {/* Role */}
        <div className="dv-smart-btn" onClick={() => setTab('general')} style={{ cursor: 'pointer' }}>
          <span className="dv-smart-btn-icon">
            <GlobeAltIcon style={{ width: 18, height: 18, color: '#7c3aed' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem' }}>
            {roleLabel}
          </span>
          <span className="dv-smart-btn-label">Rol</span>
        </div>

        <div className="dv-smart-sep" />

        {/* Interfaces */}
        <div className="dv-smart-btn" onClick={() => setTab('interfaces')} style={{ cursor: 'pointer' }}>
          <span className="dv-smart-btn-icon">
            <WifiIcon style={{ width: 18, height: 18, color: ifaceCount > 0 ? '#0369a1' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value">{ifaceCount}</span>
          <span className="dv-smart-btn-label">Interfaces</span>
        </div>

        <div className="dv-smart-sep" />

        {/* Protocols */}
        <div className="dv-smart-btn" onClick={() => setTab('routing')} style={{ cursor: 'pointer' }}>
          <span className="dv-smart-btn-icon">
            <ArrowsRightLeftIcon style={{ width: 18, height: 18, color: selectedProtocols.length > 0 ? '#ea580c' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.72rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedProtocols.length > 0 ? selectedProtocols.length + ' proto.' : '—'}
          </span>
          <span className="dv-smart-btn-label">Protocolos</span>
        </div>

        <div className="dv-smart-sep" />

        {/* HA */}
        <div className="dv-smart-btn">
          <span className="dv-smart-btn-icon">
            <SignalIcon style={{ width: 18, height: 18, color: form.ha_enabled ? '#7c3aed' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem' }}>
            {form.ha_enabled
              ? (form.ha_role === 'primary' ? 'Primario' : form.ha_role === 'secondary' ? 'Secundario' : 'HA')
              : '—'}
          </span>
          <span className="dv-smart-btn-label">Alta disp.</span>
        </div>

        <div className="dv-smart-sep" />

        {/* Location */}
        <div className="dv-smart-btn" onClick={() => setTab('ubicacion')} style={{ cursor: 'pointer' }}>
          <span className="dv-smart-btn-icon">
            <MapPinIcon style={{ width: 18, height: 18, color: form.location_name ? '#0369a1' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {form.location_name || '—'}
          </span>
          <span className="dv-smart-btn-label">Ubicación</span>
        </div>

        <div className="dv-smart-sep" />

        {/* Rack */}
        <div className="dv-smart-btn" onClick={() => setTab('ubicacion')} style={{ cursor: 'pointer' }}>
          <span className="dv-smart-btn-icon">
            <ServerStackIcon style={{ width: 18, height: 18, color: form.rack_name ? '#15803d' : '#94a3b8' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.78rem', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {form.rack_name || (form.form_factor !== 'rack' ? form.form_factor : '—')}
          </span>
          <span className="dv-smart-btn-label">Rack / Factor</span>
        </div>

        {isEditing && (
          <DocSmartButton elementType="router" elementId={router.id} elementName={form.name} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── */}
      <div className="dv-body">
        <div className={`dv-main${isCards ? ' sf2-main' : ''}`}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<GlobeAltIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(v) => sf('name', v)}
                placeholder="Nombre del router…"
                editing
                saving={saving}
                onSave={handleSave}
                onCancel={onClose}
                crumb={[vendorLabel, form.model, roleLabel, form.location_name].filter(Boolean).join(' › ')}
                badges={[
                  { text: statusInfo.label, color: form.status === 'active' ? '#16a34a' : form.status === 'standby' ? '#0369a1' : form.status === 'maintenance' ? '#f59e0b' : '#dc2626' },
                  { text: roleLabel },
                  ...(form.ha_enabled ? [{ text: form.ha_role === 'primary' ? 'HA primario' : form.ha_role === 'secondary' ? 'HA secundario' : 'HA' }] : []),
                ]}
                chips={[
                  { value: ifaceCount, label: 'interfaces', onClick: () => setTab('interfaces') },
                  { value: selectedProtocols.length, label: 'protocolos', onClick: () => setTab('routing') },
                  ...(accessCount > 0 ? [{ value: accessCount, label: 'accesos', onClick: () => setTab('access') }] : []),
                  ...(form.rack_name ? [{ value: form.rack_name, label: 'rack' }] : []),
                ]}
                extra={isEditing ? (
                  <DocSmartButton elementType="router" elementId={router.id} elementName={form.name} companyId={companyId} variant="chip" />
                ) : null}
              />
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    className="dv-record-title"
                    style={{ flex: 1 }}
                    value={form.name}
                    onChange={e => sf('name', e.target.value)}
                    placeholder="Nombre del router…"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleSuggest}
                    disabled={suggesting}
                    title="Sugerir nombre automático"
                    style={{ flexShrink: 0, padding: '4px 10px' }}
                  >
                    {suggesting ? '…' : <SparklesIcon style={{ width: 16, height: 16 }} />}
                  </button>
                </div>
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
                {tabsRt.map(t => (
                  <button
                    key={t.key}
                    className={`dv-tab-btn${tab === t.key ? ' active' : ''}`}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          >
          <fieldset className="dv-fieldset">
          {/* ── Tab: General / Resumen ── */}
          {tab === 'general' && (
            <div className={paneClass}>
              {isCards && (
                <div className="sf2-hl" style={{ marginBottom: 12 }}>
                  <div className="sf2-kpi"><span>Plataforma</span><b style={{ fontSize: '0.95rem' }}>{vendorLabel}</b></div>
                  <div className="sf2-kpi"><span>Rol</span><b style={{ fontSize: '0.95rem' }}>{roleLabel}</b></div>
                  <div className="sf2-kpi"><span>Interfaces</span><b>{ifaceCount}</b></div>
                  <div className="sf2-kpi"><span>Protocolos</span><b>{selectedProtocols.length}</b></div>
                  <div className="sf2-kpi"><span>Estado</span><b style={{ fontSize: '0.95rem' }}>{statusInfo.label}</b></div>
                </div>
              )}
              <div className={isCards ? 'sf2-grid sf2-grid--2' : undefined}>
              <Sf2Card isCards={isCards} title="Identificación y despliegue" editing>
              <div className="dv-field-grid">
                <div className="dv-field-group">
                  <label className="dv-label">Nombre *</label>
                  <input className="dv-input" value={form.name} onChange={e => sf('name', e.target.value)} placeholder="Ej. RTR-CORE-01" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Hostname / FQDN</label>
                  <input className="dv-input" value={form.hostname} onChange={e => sf('hostname', e.target.value)} placeholder="rtr-01.empresa.local" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Fabricante</label>
                  <select className="dv-select" value={form.vendor} onChange={e => sf('vendor', e.target.value)}>
                    <option value="">— Sin fabricante —</option>
                    {manufacturerOptions.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                    {form.vendor && !manufacturerOptions.find(m => m.name === form.vendor) && (
                      <option value={form.vendor}>{form.vendor}</option>
                    )}
                  </select>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Modelo</label>
                  <select className="dv-select" value={form.model} onChange={e => sf('model', e.target.value)}>
                    <option value="">— Sin modelo —</option>
                    {filteredModelOptions.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                    {form.model && !modelOptions.find(m => m.name === form.model) && (
                      <option value={form.model}>{form.model}</option>
                    )}
                  </select>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Versión firmware / OS</label>
                  <input className="dv-input" value={form.firmware_version} onChange={e => sf('firmware_version', e.target.value)} placeholder="16.12.4, 7.14…" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Versión IOS / JunOS / RouterOS</label>
                  <input className="dv-input" value={form.ios_version} onChange={e => sf('ios_version', e.target.value)} placeholder="IOS XE 17.6.5, RouterOS 7.14…" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Rol</label>
                  <select className="dv-select" value={form.router_role} onChange={e => sf('router_role', e.target.value)}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Throughput (Gbps)</label>
                  <input className="dv-input" type="number" min="0" step="0.1" value={form.throughput_gbps} onChange={e => sf('throughput_gbps', e.target.value)} placeholder="1.0" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Número de serie</label>
                  <input className="dv-input" value={form.serial_number} onChange={e => sf('serial_number', e.target.value)} />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Asset tag</label>
                  <input className="dv-input" value={form.asset_tag} onChange={e => sf('asset_tag', e.target.value)} />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Estado</label>
                  <select className="dv-select" value={form.status} onChange={e => sf('status', e.target.value)}>
                    <option value="active">Activo</option>
                    <option value="standby">Standby</option>
                    <option value="maintenance">Mantenimiento</option>
                    <option value="decom">Decommissioned</option>
                  </select>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Circuito ISP vinculado</label>
                  <select className="dv-select" value={form.isp_circuit_id} onChange={e => sf('isp_circuit_id', e.target.value)}>
                    <option value="">— Sin circuito —</option>
                    {circuits.map(c => (
                      <option key={c.id} value={c.id}>{c.label || c.circuit_id_provider} ({c.operator_name || ''})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Capabilities section */}
              <div className="dv-section-header" style={{ marginTop: 28 }}>Capacidades</div>
              <div className="dv-field-grid">
                <div className="dv-field-group">
                  <label className="dv-label">Alta disponibilidad</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                    <input type="checkbox" id="ha_enabled" checked={form.ha_enabled} onChange={e => sf('ha_enabled', e.target.checked)} style={{ width: 16, height: 16 }} />
                    <label htmlFor="ha_enabled" style={{ fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>Habilitada</label>
                  </div>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Rol en clúster HA</label>
                  <select className="dv-select" value={form.ha_role} onChange={e => sf('ha_role', e.target.value)} disabled={!form.ha_enabled}>
                    <option value="standalone">Standalone (sin HA)</option>
                    <option value="primary">Primario (activo)</option>
                    <option value="secondary">Secundario (pasivo)</option>
                  </select>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">VPN</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                    <input type="checkbox" id="vpn_capable" checked={form.vpn_capable} onChange={e => sf('vpn_capable', e.target.checked)} style={{ width: 16, height: 16 }} />
                    <label htmlFor="vpn_capable" style={{ fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>Habilitada</label>
                  </div>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">MPLS</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                    <input type="checkbox" id="mpls_capable" checked={form.mpls_capable} onChange={e => sf('mpls_capable', e.target.checked)} style={{ width: 16, height: 16 }} />
                    <label htmlFor="mpls_capable" style={{ fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>Habilitado</label>
                  </div>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">NAT</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                    <input type="checkbox" id="nat_enabled" checked={form.nat_enabled} onChange={e => sf('nat_enabled', e.target.checked)} style={{ width: 16, height: 16 }} />
                    <label htmlFor="nat_enabled" style={{ fontSize: '0.85rem', color: '#334155', cursor: 'pointer' }}>Habilitado</label>
                  </div>
                </div>
              </div>
              </Sf2Card>
            {isCards && (
              <Sf2Card isCards title="Ubicación" editing>{routerUbicacionContent}</Sf2Card>
            )}
            </div>
            </div>
          )}

          {/* ── Tab: Routing ── */}
          {tab === 'routing' && (
            <div className={paneClass}>
              <div className="dv-section-header">Protocolos de enrutamiento</div>
              <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 12 }}>
                Selecciona los protocolos configurados en este router.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                {ROUTING_PROTOCOLS.map(p => {
                  const active = selectedProtocols.includes(p.value);
                  return (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => toggleProtocol(p.value)}
                      style={{
                        padding: '5px 14px', borderRadius: 20, fontSize: '0.82rem', fontWeight: 600,
                        border: active ? '2px solid #2563eb' : '1px solid #d1d5db',
                        background: active ? '#dbeafe' : '#fff',
                        color: active ? '#1e40af' : '#475569',
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}
                    >
                      {active && '✓ '}{p.label}
                    </button>
                  );
                })}
              </div>

              <div className="dv-field-grid">
                <div className="dv-field-group">
                  <label className="dv-label">ASN (Autonomous System Number)</label>
                  <input className="dv-input" type="number" value={form.asn} onChange={e => sf('asn', e.target.value)} placeholder="Ej. 65001" />
                  <small style={{ color: '#64748b', fontSize: '0.75rem' }}>Solo si usa BGP o routing inter-AS.</small>
                </div>
              </div>

              {/* Active protocols summary */}
              {selectedProtocols.length > 0 && (
                <div style={{ marginTop: 20, padding: '12px 16px', background: 'rgba(16,185,129,.15)', borderRadius: 8, border: '1px solid rgba(16,185,129,.4)' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#166534', marginBottom: 4 }}>
                    Protocolos activos: {selectedProtocols.length}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#15803d' }}>
                    {protocolsText}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Interfaces ── */}
          {tab === 'interfaces' && (
            <div className={paneClass}>
              {/* Interface add form */}
              {showIfaceForm && (
                <div style={{ background: 'var(--bg-toolbar)', border: '1px solid var(--border)', borderRadius: 8, padding: '18px 20px', marginBottom: 20 }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 14 }}>
                    {editingIface ? 'Editar interfaz' : 'Nuevo interfaz'}
                  </div>
                  <div className="dv-field-grid">
                    <div className="dv-field-group">
                      <label className="dv-label">Nombre *</label>
                      <input className="dv-input" value={ifaceForm.name}
                        onChange={e => setIfaceForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="GigabitEthernet0/0, ether1…" />
                    </div>
                    <div className="dv-field-group">
                      <label className="dv-label">Tipo</label>
                      <select className="dv-select" value={ifaceForm.iface_type}
                        onChange={e => setIfaceForm(f => ({ ...f, iface_type: e.target.value }))}>
                        {IFACE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div className="dv-field-group">
                      <label className="dv-label">MAC Address</label>
                      <input className="dv-input" value={ifaceForm.mac_address}
                        onChange={e => setIfaceForm(f => ({ ...f, mac_address: e.target.value }))}
                        placeholder="aa:bb:cc:dd:ee:ff" />
                    </div>
                    <div className="dv-field-group">
                      <label className="dv-label">Velocidad (Mbps)</label>
                      <input className="dv-input" type="number" value={ifaceForm.speed_mbps}
                        onChange={e => setIfaceForm(f => ({ ...f, speed_mbps: e.target.value }))} placeholder="1000" />
                    </div>
                    <div className="dv-field-group">
                      <label className="dv-label">VLAN ID</label>
                      <input className="dv-input" type="number" value={ifaceForm.vlan_id}
                        onChange={e => setIfaceForm(f => ({ ...f, vlan_id: e.target.value }))} placeholder="10" />
                    </div>
                    <div className="dv-field-group">
                      <label className="dv-label">Subred IPAM</label>
                      <select className="dv-select" value={ifaceForm.subnet_id}
                        onChange={e => setIfaceForm(f => ({ ...f, subnet_id: e.target.value }))}>
                        <option value="">— Sin subred —</option>
                        {subnets.map(s => (
                          <option key={s.id} value={s.id}>{s.cidr} {s.site ? `(${s.site})` : ''}</option>
                        ))}
                      </select>
                    </div>
                    <div className="dv-field-group" style={{ gridColumn: '1 / -1' }}>
                      <label className="dv-label">Descripción</label>
                      <input className="dv-input" value={ifaceForm.description}
                        onChange={e => setIfaceForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Descripción opcional" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <input type="checkbox" id="iface_enabled" checked={ifaceForm.enabled}
                      onChange={e => setIfaceForm(f => ({ ...f, enabled: e.target.checked }))} />
                    <label htmlFor="iface_enabled" style={{ fontSize: '0.82rem', color: '#334155', cursor: 'pointer' }}>
                      Interfaz habilitado
                    </label>
                  </div>
                  {ifaceError && <div style={{ color: '#dc2626', fontSize: '0.82rem', marginTop: 8 }}>{ifaceError}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                    <button className="dv-btn-save" onClick={handleIfaceSave} disabled={ifaceSaving}>
                      <CheckIcon style={{ width: 14, height: 14 }} />
                      {ifaceSaving ? 'Guardando…' : editingIface ? 'Actualizar' : 'Añadir'}
                    </button>
                    <button className="dv-btn-discard" onClick={() => { setShowIfaceForm(false); setEditingIface(null); }}>
                      <XMarkIcon style={{ width: 14, height: 14 }} /> Cancelar
                    </button>
                  </div>
                </div>
              )}

              {/* Add button */}
              {!showIfaceForm && (
                <div style={{ marginBottom: 16 }}>
                  {!isEditing && (
                    <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 8 }}>
                      Guarda el router primero para poder añadir interfaces.
                    </p>
                  )}
                  <button className="dv-btn-save" onClick={openNewIface} disabled={!isEditing} style={{ opacity: isEditing ? 1 : 0.5 }}>
                    <PlusIcon style={{ width: 15, height: 15 }} /> Añadir interfaz
                  </button>
                </div>
              )}

              {/* Interface table */}
              {ifaces.length === 0 ? (
                <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>
                  Sin interfaces configurados. Añade las interfaces del router (WAN, LAN, Loopback…).
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                      <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Nombre</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Tipo</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Subred</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>MAC</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Velocidad</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>VLAN</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>Estado</th>
                      <th style={{ padding: '6px 10px' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {ifaces.map(iface => {
                      const tc = IFACE_TYPE_COLOR[iface.iface_type] || IFACE_TYPE_COLOR.other;
                      return (
                        <tr key={iface.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 10px', fontWeight: 600 }}>{iface.name}</td>
                          <td style={{ padding: '8px 10px' }}>
                            <span style={{ background: tc.bg, color: tc.color, borderRadius: 4, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 600 }}>
                              {iface.iface_type_label || iface.iface_type}
                            </span>
                          </td>
                          <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: '0.8rem', color: '#475569' }}>
                            {iface.subnet_cidr || <span style={{ color: '#94a3b8' }}>—</span>}
                          </td>
                          <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: '0.8rem', color: '#475569' }}>
                            {iface.mac_address || '—'}
                          </td>
                          <td style={{ padding: '8px 10px', color: '#475569' }}>
                            {iface.speed_mbps ? `${iface.speed_mbps} Mbps` : '—'}
                          </td>
                          <td style={{ padding: '8px 10px', color: '#475569' }}>
                            {iface.vlan_id || '—'}
                          </td>
                          <td style={{ padding: '8px 10px' }}>
                            {iface.enabled
                              ? <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>● Activo</span>
                              : <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>○ Desactivado</span>
                            }
                          </td>
                          <td style={{ padding: '8px 10px' }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb' }} onClick={() => openEditIface(iface)} title="Editar">
                                <PencilSquareIcon style={{ width: 16, height: 16 }} />
                              </button>
                              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }} onClick={() => handleIfaceDelete(iface)} title="Eliminar">
                                <TrashIcon style={{ width: 16, height: 16 }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {ifaces.length > 0 && (
                <p style={{ marginTop: 16, fontSize: '0.78rem', color: '#64748b' }}>
                  💡 Para asignar IPs a cada interfaz, ve a la pestaña <strong>IPAM / IPs</strong> y usa el campo <em>Interfaz ref.</em> con el nombre del interfaz.
                </p>
              )}
            </div>
          )}

          {/* ── Tab: IPAM ── */}
          {tab === 'ipam' && (
            <div className={paneClass}>
              {isEditing ? (
                <>
                  <ElementIpPanel
                    objectType="router"
                    objectId={router.id}
                    companyId={companyId}
                    elementName={form.name}
                    interfaces={ifaces}
                  />
                  <NetworkConnectionsPanel
                    objectType="router"
                    objectId={router.id}
                    companyId={companyId}
                    elementName={form.name}
                  />
                </>
              ) : (
                <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>
                  Guarda el router primero para gestionar sus IPs.
                </p>
              )}
            </div>
          )}
          {/* ── Tab: Accesos ── */}
          {tab === 'access' && (
            <div className={paneClass}>
              {isEditing ? (
                <DeviceAccessPanel
                  objectType="router"
                  objectId={router?.id}
                  companyId={companyId}
                  elementName={form.name}
                  onCountChange={setAccessCount}
                />
              ) : (
                <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>
                  Guarda el router primero para documentar sus accesos.
                </p>
              )}
            </div>
          )}

          {/* ── Tab: Ubicación (solo ficha clásica; en tarjetas va a la derecha de General) ── */}
          {!isCards && tab === 'ubicacion' && (
            <div className={paneClass}>
              <Sf2Card isCards={isCards} title="Ubicación física" editing>
                {routerUbicacionContent}
              </Sf2Card>
            </div>
          )}

          {/* ── Tab: Alimentación ── */}
          {tab === 'power' && (
            <div className={paneClass}>
              {router.rack_element_id ? (
                <>
                  <div className="dv-section">
                    <SwitchPsuSection
                      elementId={router.rack_element_id}
                      companyId={companyId}
                    />
                  </div>
                  <div className="dv-section" style={{ marginTop: 24 }}>
                    <div className="dv-section-title">Conexiones PDU / Alimentación eléctrica</div>
                    <PowerConnectionsPanel
                      consumerType="rack_element"
                      consumerId={router.rack_element_id}
                      companyId={companyId}
                      rackId={form.rack_id ? Number(form.rack_id) : undefined}
                      elementName={form.name}
                    />
                  </div>
                </>
              ) : (
                <div className="dv-section">
                  <p className="dv-hint">Este router no tiene entrada de rack asociada. Asígnalo a un rack para gestionar su alimentación.</p>
                </div>
              )}
            </div>
          )}

          </fieldset>
          <FormDirtyDock show={isCards && isDirty} saving={saving} onDiscard={handleDiscard} onSave={handleSave} />
          </FormCardsShell>
        </div>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="router"
          ciId={router?.id}
          tipoLegible="el router"
        />
      </div>
    </div>
  );
}
